import {
    BaseQueryFn,
    FetchArgs,
    FetchBaseQueryError,
    FetchBaseQueryMeta,
    QueryDefinition,
} from '@reduxjs/toolkit/dist/query';
import { UseLazyQuery } from '@reduxjs/toolkit/dist/query/react/buildHooks';
import { useDebounce } from '@uidotdev/usehooks';
import { MainFilterInForm, QuickFilterForm } from 'components/tables/list-table/ListTableTypes';
import { KeyboardEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { PaginationProps, QueryResponse } from 'types/paginationAndFilter';
import { reversibleNameSearchFields, rotateNameSearchTerm, useLastNameFirst } from 'utils/nameOrder';
import { escapeRsqlValue } from 'utils/rsql.ts';

export interface PaginatedListProps<T, TParams extends PaginationProps = PaginationProps> {
    fetch: UseLazyQuery<
        QueryDefinition<
            TParams,
            BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError, Record<string, unknown>, FetchBaseQueryMeta>,
            string,
            QueryResponse<T>,
            'portalAPI'
        >
    >;
    initialSearchValue?: string;
    filters?: string;
    passiveFilters?: string;
    pageSize?: number;
    page?: number;
    sort?: string | string[];
    id?: string | number;
    isEnabled?: boolean;
    searchField: string | string[];
    /** defaults to `=lk=` */
    searchComparator?: string;
    /** If true, pass search as direct parameter (non-RSQL) instead of building a filter */
    useCustomSearch?: boolean;
    getFilter?: (qf: QuickFilterForm, mf: MainFilterInForm[]) => string | undefined;
    quickFilters?: QuickFilterForm;
    mainFilters?: MainFilterInForm[];
    additionalParams?: Omit<TParams, keyof PaginationProps>;
    /**
     * Reorders the loaded page window before it is handed to the dropdown. Endpoints that cannot
     * express a priority order server-side use this to float a known-ahead subset to the top; the
     * current search text is passed so the reordering can respect what the user is filtering by.
     * Grouping relies on adjacency, so any grouped option set must be ordered here or by `sort`.
     */
    transformOptions?: (options: T[], search: string) => T[];
    /**
     * Requests the first page again when `filters` changes while the list is enabled. Without it an open
     * popup keeps the page of the filters it opened with, and asks for its next page under the new ones.
     */
    refetchOnFiltersChange?: boolean;
}

export interface PaginatedListOptionProps {
    getOptionLabel: string | number;
    getOptionIdentifier: string | number;
    /**
     * Ensure that the value specified in `groupByField` is also included in the `fetch` sort options (`paginated.sort`)
     * with the same value to avoid duplicate grouping issues.
     */
    groupByField?: string;
}

export const usePaginatedList = <TOption, TParams extends PaginationProps = PaginationProps>(
    props: PaginatedListProps<TOption, TParams>
) => {
    const [fetch, { data, currentData, isLoading, isFetching, isError }] = props.fetch();
    const isEnabled = props.isEnabled === undefined ? true : props.isEnabled;
    const [isInitialFetchDone, setIsInitialFetchDone] = useState(false);
    // Whether a first-page request has gone out since the list was last enabled. Until one has, the
    // page RTK reports is for the args of an earlier open.
    const [isFirstPageIssued, setIsFirstPageIssued] = useState(false);
    if (!isEnabled && isFirstPageIssued) {
        setIsFirstPageIssued(false);
    }
    const [currentPageIndex, setCurrentPageIndex] = useState(0);
    const [inputValue, setInputValue] = useState(props.initialSearchValue ?? '');
    const search = useDebounce(inputValue, 300);
    const { quickFilters, mainFilters, getFilter, searchField, searchComparator } = props;
    const lastNameFirst = useLastNameFirst();

    // Reset isInitialFetchDone when isEnabled changes from false to true
    // This fixes the bug where dropdowns don't load data on first open
    const prevIsEnabledRef = useRef(isEnabled);
    useEffect(() => {
        if (isEnabled && !prevIsEnabledRef.current) {
            setIsInitialFetchDone(false);
        }
        prevIsEnabledRef.current = isEnabled;
    }, [isEnabled]);
    const dynamicFilter = useMemo(() => {
        return quickFilters || mainFilters ? getFilter?.(quickFilters ?? {}, mainFilters ?? []) : undefined;
    }, [getFilter, quickFilters, mainFilters]);

    // Stabilize additionalParams by value to prevent unnecessary refetch recreations
    const additionalParamsKey = JSON.stringify(props.additionalParams);
    const stableAdditionalParams = useMemo(
        () => props.additionalParams,
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [additionalParamsKey]
    );

    const refetch = useCallback(
        async ({ pageIndex = 0, search = '' }: { pageIndex?: number; search?: string }) => {
            if (!isEnabled) {
                return;
            }

            if (props.useCustomSearch) {
                const params: Record<string, unknown> = {
                    ...stableAdditionalParams,
                    page: pageIndex,
                    size: props.pageSize ?? 50,
                    sort: props.sort,
                };

                if (search?.length > 0) {
                    const searchFieldArray = Array.isArray(searchField) ? searchField : [searchField];
                    params[searchFieldArray[0]] = search;
                }

                return fetch(params as TParams, false);
            }

            const searchFieldArray = Array.isArray(searchField) ? searchField : [searchField];
            // Under LAST_NAME_FIRST, also try the reversed word order against Employee/Party name
            // fields, so a "Last First" typed query still matches the (always first-last) stored value.
            const reversedSearch = lastNameFirst ? rotateNameSearchTerm(search) : null;
            const reversibleFields = reversedSearch ? reversibleNameSearchFields(searchFieldArray) : new Set<string>();
            const comparator = searchComparator ?? '=lk=';
            const filters =
                search?.length > 0
                    ? [
                          `(${searchFieldArray.map((field) =>
                              reversibleFields.has(field)
                                  ? `${field}${comparator}('${search}','${reversedSearch}')`
                                  : `${field}${comparator}'${search}'`
                          )})`,
                          props.filters,
                          dynamicFilter,
                      ]
                          .filter(Boolean)
                          .join(';')
                    : [props.filters, dynamicFilter].filter(Boolean).join(';');

            return fetch(
                {
                    ...stableAdditionalParams,
                    filters,
                    page: pageIndex,
                    pageSize: props.pageSize ?? 50,
                    sort: props.sort,
                } as TParams,
                false
            );
        },
        [
            fetch,
            props.pageSize,
            props.filters,
            props.useCustomSearch,
            searchField,
            searchComparator,
            props.sort,
            isEnabled,
            dynamicFilter,
            stableAdditionalParams,
            lastNameFirst,
        ]
    );

    const isFetchingRef = useRef(false);
    // The search the last first-page request went out with. A search that arrives while one is in
    // flight is not recorded, so the effect below still sees it as new once that request settles.
    const lastSearchRef = useRef<string | null>(null);
    const lastFiltersRef = useRef(props.filters);
    const [settledCount, setSettledCount] = useState(0);

    const initialGet = useCallback(() => {
        // A request still in flight from an earlier open is the current one: RTK reports its page.
        if (isEnabled) {
            setIsFirstPageIssued(true);
        }
        if (isFetchingRef.current) {
            return;
        }
        isFetchingRef.current = true;
        lastSearchRef.current = search;
        lastFiltersRef.current = props.filters;
        setCurrentPageIndex(0);
        refetch({ search, pageIndex: 0 }).finally(() => {
            setIsInitialFetchDone(true);
            isFetchingRef.current = false;
            setSettledCount((count) => count + 1);
        });
    }, [refetch, search, isEnabled, props.filters]);

    const followsFilters = !!props.refetchOnFiltersChange && isEnabled;
    useEffect(() => {
        // Only fetch if search actually changed, initial fetch not done, or the filters a list follows changed
        const areFiltersNew = followsFilters && lastFiltersRef.current !== props.filters;
        if (lastSearchRef.current !== search || !isInitialFetchDone || areFiltersNew) {
            initialGet();
        }
    }, [search, initialGet, isInitialFetchDone, settledCount, followsFilters, props.filters]);

    // `data` is RTK's latest result for any args: while even a cached entry refetches, it is the page an
    // earlier search left behind. `currentData` is only ever the requested args' page, so that is the one
    // shown, and `data` only fills in while new args have no page yet — a search typed into an open
    // popup keeps the previous results until its own land. Once their request has failed, RTK still
    // hands back the earlier page in `data`, so nothing fills in and the list shows it has no options.
    const page = currentData ?? (isError ? undefined : data);

    const handleLoadMore = useCallback(() => {
        if (page?.last) return;
        // A first page asked for since under another search or other filters started another list at
        // page 0: this answer is a page of the list it replaced, and must not move the new one on.
        const [listSearch, listFilters] = [lastSearchRef.current, lastFiltersRef.current];
        refetch({ pageIndex: currentPageIndex + 1, search }).then(() => {
            if (lastSearchRef.current === listSearch && lastFiltersRef.current === listFilters) {
                setCurrentPageIndex((prev) => prev + 1);
            }
        });
    }, [page, currentPageIndex, refetch, search]);

    const handleSearchChange = useCallback(
        (input: string) => {
            // Only escape RSQL values when using filter-based search
            // Direct search APIs should receive the raw input
            setInputValue(props.useCustomSearch ? input : escapeRsqlValue(input));
        },
        [setInputValue, props.useCustomSearch]
    );

    // A mock that reports neither `isFetching` nor `currentData` falls back to `data`.
    const hasPageForArgs = (isFetching ? currentData : page) !== undefined;

    const transformOptions = props.transformOptions;
    const dataToDisplay = useMemo(() => {
        const loaded = (isEnabled ? page?.content || [] : []) as TOption[];
        return transformOptions ? transformOptions(loaded, search) : loaded;
    }, [page?.content, isEnabled, transformOptions, search]);

    return {
        dataToDisplay,
        handleLoadMore,
        // No page, as after a failed request, has no next one: its loader row would never resolve.
        hasNextPage: page !== undefined && !page.last,
        isLoading: isLoading || isFetching,
        isError,
        handleSearchChange,
        // A settled request counts as landed whether it brought a page or an error, so an error still opens.
        isAwaitingFirstPage: isEnabled && (!isFirstPageIssued || ((isLoading || isFetching) && !hasPageForArgs)),
    };
};

/**
 * {@link usePaginatedList} for a combobox that fetches its options when it opens, with the popup's open
 * state. Opening only requests the popup: the first page is fetched while the field shows it is busy,
 * and the popup opens once the page for the requested search and filters is in, at its final height —
 * painting the loading state, or an earlier search's page, first would resize it under the pointer. A
 * page cached for those args opens at once, and a search typed into an open popup refetches in place.
 *
 * While the popup is held back the combobox counts itself closed, and a closed Base UI combobox reports
 * neither a blur nor an Escape as a close — the kit's inputs let Escape bubble on — and takes a second
 * press on its chevron as another open. `dropPendingOpen`, `onInputKeyDown` and `setOpen` answer those
 * instead, and Enter too, so each drops the request and keeps the value as it would close an open popup.
 *
 * A dropped request never opened the popup, so Base UI never runs the close that puts the input text
 * back. A field whose text Base UI owns passes `actionsRef` to its combobox root; the unmount it is then
 * handed is called after every close, dropped requests included, and restores the text exactly as a
 * close does: the selected label, or empty. `onClose` also runs on every close — where a field that
 * controls its own input text clears it.
 *
 * `openOnMount` requests the open with the first render, held the same way, for a field that is mounted
 * by the click meant to open it.
 */
export const usePaginatedPopup = <TOption>(
    paginated: PaginatedListProps<TOption>,
    onClose?: () => void,
    openOnMount = false
) => {
    const [isOpenRequested, setIsOpenRequested] = useState(openOnMount);
    const list = usePaginatedList<TOption>({
        ...paginated,
        isEnabled: paginated.isEnabled === false ? false : isOpenRequested,
    });
    const { handleSearchChange } = list;
    // Held only until first shown: a search typed into the open popup, or its next page, refetches in place.
    const [hasShown, setHasShown] = useState(false);
    const isOpen = isOpenRequested && (hasShown || !list.isAwaitingFirstPage);
    if (isOpen && !hasShown) {
        setHasShown(true);
    } else if (!isOpenRequested && hasShown) {
        setHasShown(false);
    }
    const isOpenPending = isOpenRequested && !isOpen;

    // Base UI's `Combobox.Root.Actions`, spelled out: its declaring module is internal to the package.
    const actionsRef = useRef<{ unmount: () => void } | null>(null);
    useEffect(() => {
        if (!isOpenRequested) {
            actionsRef.current?.unmount();
        }
    }, [isOpenRequested]);

    const setOpen = useCallback(
        (open: boolean, details?: { reason?: string }) => {
            const next = open && !(isOpenPending && details?.reason === 'trigger-press');
            setIsOpenRequested(next);
            if (!next) {
                handleSearchChange('');
                onClose?.();
            }
        },
        [handleSearchChange, onClose, isOpenPending]
    );

    return {
        ...list,
        isOpen,
        isOpenPending,
        actionsRef,
        setOpen,
        dropPendingOpen: () => {
            if (isOpenPending) {
                setOpen(false);
            }
        },
        onInputKeyDown: (event: KeyboardEvent<HTMLInputElement> & { preventBaseUIHandler?: () => void }) => {
            if (!isOpenPending) {
                return;
            }
            if (event.key === 'Escape') {
                // Consumed here as an open popup would consume it, so an enclosing dialog stays open.
                event.preventBaseUIHandler?.();
                event.stopPropagation();
                setOpen(false);
            } else if (event.key === 'Enter') {
                // Not consumed: with nothing highlighted, an open popup closes and lets the form submit.
                setOpen(false);
            }
        },
    };
};
